/*
 * Custom video — deferred hydration
 * ---------------------------------
 * Videos rendered by snippets/video_new.liquid ship with their <source>
 * carrying `data-sourcesrc` instead of `src` + `preload="none"`, so the
 * browser does not request the video bytes during initial page load.
 *
 * This script lives behind the first user interaction (mousemove/touchstart).
 * On the first input:
 *   1. Find every `video[data-video-type="native"]` on the page (desktop AND mobile variants).
 *   2. Copy `data-sourcesrc` onto each <source>'s `src` attribute.
 *   3. Call `video.load()` so the element picks up the new source.
 *   4. If autoplay is declared, force `muted` + `playsInline` and attempt
 *      `.play()` (swallowing rejections — some browsers still block it).
 *
 * A MutationObserver re-runs the same logic when new videos are injected
 * later (e.g. Section Rendering API re-renders, theme editor edits).
 *
 * Idempotent: each hydrated video is tagged with `data-hydrated="1"`.
 */

(function () {
  'use strict';

  function hydrateVideo(video) {
    if (!video || video.dataset.hydrated === '1') return;

    video.querySelectorAll('source[data-sourcesrc]').forEach(function (source) {
      var newSrc = source.dataset.sourcesrc;
      if (newSrc && source.getAttribute('src') !== newSrc) {
        source.setAttribute('src', newSrc);
      }
    });

    try { video.load(); } catch (e) { /* noop */ }

    if (
      video.hasAttribute('autoplay') ||
      video.hasAttribute('data-video-autoplay') ||
      video.dataset.videoAutoplay === 'true'
    ) {
      video.muted = true;
      video.playsInline = true;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay blocked, ignore */ });
    }

    video.dataset.hydrated = '1';
  }

  function primeExisting() {
    document.querySelectorAll('video[data-video-type="native"]').forEach(hydrateVideo);
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (!m.addedNodes || !m.addedNodes.length) return;
      m.addedNodes.forEach(function (node) {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches && node.matches('video[data-video-type="native"]')) {
          hydrateVideo(node);
        } else if (node.querySelectorAll) {
          node.querySelectorAll('video[data-video-type="native"]').forEach(hydrateVideo);
        }
      });
    });
  });

  function startObserver() {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function init() {
    // Always watch for videos inserted AFTER load — e.g. a gallery video clicked into the main
    // stage (its markup lives in an inert <template> until then) — and hydrate them immediately,
    // so they play on the very interaction that inserted them.
    startObserver();

    // Videos already in the document at load (e.g. the hero banner) stay deferred until the first
    // user interaction, so their bytes are not fetched during initial page load.
    window.addEventListener('mousemove', primeExisting, { once: true, passive: true });
    window.addEventListener('touchstart', primeExisting, { once: true, passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
